import { Component, ErrorInfo, ReactNode } from 'react'
import { Box, Container, Typography, Button, Paper } from '@mui/material'
import { ErrorOutline } from '@mui/icons-material'

interface ErrorBoundaryProps {
  children: ReactNode
  /**
   * Optional fallback component to render on error
   */
  fallback?: ReactNode
  /**
   * Optional callback when error occurs
   * @param error - The error that was caught
   * @param errorInfo - Additional error information from React
   */
  // eslint-disable-next-line no-unused-vars
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component for graceful error handling
 * Catches React component errors and displays user-friendly fallback UI
 *
 * @example
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // In production, you would log to error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo })
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 8,
          }}
        >
          <Container maxWidth="lg">
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, sm: 4, md: 6 },
                textAlign: 'center',
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: { xs: 64, sm: 80 },
                  color: 'error.main',
                  mb: 2,
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Something Went Wrong
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  color: 'text.secondary',
                  mb: 4,
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                We apologize for the inconvenience. An unexpected error has
                occurred. Please try refreshing the page or contact support if
                the problem persists.
              </Typography>

              {import.meta.env.DEV && this.state.error && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    textAlign: 'left',
                    bgcolor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'error.main', fontWeight: 600 }}
                  >
                    Error Details (Development Only):
                  </Typography>
                  <Typography
                    component="pre"
                    sx={{
                      mt: 1,
                      color: 'text.secondary',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {this.state.error.toString()}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={this.handleReset}
                  sx={{ minWidth: 120 }}
                >
                  Try Again
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => (window.location.href = '/')}
                  sx={{ minWidth: 120 }}
                >
                  Go Home
                </Button>
              </Box>
            </Paper>
          </Container>
        </Box>
      )
    }

    return this.props.children
  }
}
